"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Cloud, ShieldCheck, TrendingUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function LinxMicrovixOErpDasFranquias() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: imgRef,
    isVisible: isImgVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="linx-microvix-o-erp-das-franquias" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      <style dangerouslySetInnerHTML={{
      __html: `
        :root {
          --fume-bg: hsla(0, 0%, 15%, 0.7);
        }
        :root, .dark {
          --background: var(--fume-bg);
        }
        section, .card, .popover, .modal, nav, footer, header, article, aside, dialog {
          background-color: var(--fume-bg) !important;
          backdrop-filter: blur(12px) !important;
        }
        .bg-card, .bg-popover, .bg-modal, .bg-section {
          background-color: var(--fume-bg) !important;
          backdrop-filter: blur(12px) !important;
        }
        section *, .card *, .popover *, .modal * {
          color: var(--foreground);
        }
      `
    }} />

      {}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592892/img/microvix_cloud_network_bg.png)] bg-cover bg-center opacity-15 mix-blend-luminosity"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/40"></div>
        
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[150px] pointer-events-none"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={textRef} className={`transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-8 border border-primary/20 backdrop-blur-sm">
              <Cloud className="size-4" />
              <span>Solução 100% em Nuvem</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6 text-white leading-[1.1]">
              O ERP Definitivo para <span className="text-primary">Franquias</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-xl leading-relaxed">
              Transforme a gestão da sua rede com o Linx Microvix. Controle total de estoque, PDV ágil, gestão financeira centralizada e tecnologia líder para escalar seus resultados no varejo.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto bg-[#FF006E] text-white rounded-md shadow-lg hover:shadow-xl hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 text-base px-8 h-14 font-semibold">
                <Link to="/ContactsPage">Solicitar demonstração</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 rounded-md text-base px-8 h-14 font-semibold transition-colors duration-300">
                <Link to="https://api.whatsapp.com/send?phone=5527992826653&text=Bem%20vindo%20a%20Franquia%20Linx%20Vila%20Velha.">Falar no WhatsApp</Link>
              </Button>
            </div>

            {}
            <div className="mt-12 pt-8 border-t border-border/40 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm font-medium text-muted-foreground">
              <div className="flex items-center gap-2">
                <ShieldCheck className="size-5 text-primary" />
                Implantação Guiada
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="size-5 text-primary" />
                Alta Performance
              </div>
            </div>
          </div>

          {}
          <div ref={imgRef} className={`relative transition-all duration-1000 delay-300 ease-out ${isImgVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="relative rounded-2xl overflow-hidden border border-border/30 shadow-2xl shadow-black/50 bg-card/40 backdrop-blur-md p-2 lg:p-4 transform lg:-rotate-2 hover:rotate-0 transition-transform duration-500">
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/5 to-transparent opacity-50"></div>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1592892/img/microvix_dashboard_mockup.png" alt="Interface do Sistema Linx Microvix" className="rounded-xl w-full h-auto object-cover relative z-10 border border-border/20" />
              
              {}
              <div className="absolute -bottom-6 -left-6 bg-card border border-border/50 shadow-xl rounded-xl p-4 flex items-center gap-4 z-20 animate-bounce" style={{
              animationDuration: '3s'
            }}>
                <div className="size-12 rounded-full bg-primary/20 flex items-center justify-center">
                  <TrendingUp className="size-6 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">Crescimento</p>
                  <p className="text-lg font-bold text-foreground">+45% Vendas</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}